/**
 * Real-time WebSocket client for LostDC Network.
 *
 * Auto-reconnects with exponential backoff. Exposes a React context with:
 *   - `send(op, payload)` — send an op to the server
 *   - `subscribe(op, cb)`  — subscribe to a specific op ('message'|'typing'|'read'|'presence'|'notification'|'reaction'|'edit'|'delete'|'conv_update')
 *   - `presence` map (user_id → { online, last_seen })
 *   - `connected` boolean
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { sfx } from "@/lib/sounds";

const RealtimeCtx = createContext(null);

function backendWsUrl(token) {
  const http = process.env.REACT_APP_BACKEND_URL || "";
  const ws = http.replace(/^http/, "ws");
  return `${ws}/api/ws?token=${encodeURIComponent(token)}`;
}

const NOTIF_SOUND_MAP = {
  message: "chime_message",
  mention: "chime_mention",
  follow: "chime_follow",
  follow_request: "chime_follow",
  like: "chime_community",
  comment: "chime_community",
  approved: "chime_approved",
  rejected: "no",
  system: "chime_announcement",
  event: "chime_event",
  creator: "chime_creator",
};

function playNotifSound(nType, prefs) {
  if (prefs?.master_enabled === false) return;
  if (prefs?.sounds_enabled === false) return;
  const typeCfg = prefs?.types?.[nType];
  if (typeCfg?.enabled === false) return;
  const chosen = typeCfg?.sound || NOTIF_SOUND_MAP[nType] || "chime_message";
  const fn = sfx[chosen] || sfx.pling;
  try { fn(); } catch { /* ignore */ }
}

async function showBrowserNotif(n, prefs) {
  if (prefs?.master_enabled === false) return;
  if (prefs?.browser_enabled === false) return;
  if (!("Notification" in window)) return;
  if (Notification.permission === "denied") return;
  if (Notification.permission !== "granted") {
    try { const r = await Notification.requestPermission(); if (r !== "granted") return; } catch { return; }
  }
  try {
    const title = n.actor_name ? `${n.actor_name}` : "LostDC Network";
    new Notification(title, {
      body: `${n.subject_title || ""} ${n.body || ""}`.trim().slice(0, 160),
      icon: n.actor_avatar || "/logo.png",
      tag: n.id,
    });
  } catch { /* ignore */ }
}

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [presence, setPresence] = useState(() => new Map());
  const wsRef = useRef(null);
  const attemptsRef = useRef(0);
  const closedByUsRef = useRef(false);
  const subsRef = useRef(new Map()); // op -> Set(cb)
  const prefsRef = useRef({});

  // Load prefs (best-effort — the auth /notification-prefs is user-scoped)
  useEffect(() => {
    if (!user) return;
    const cached = localStorage.getItem("lostdc:notif-prefs");
    if (cached) { try { prefsRef.current = JSON.parse(cached) || {}; } catch { /* ignore */ } }
    // Also fetch from backend
    import("@/lib/auth").then(({ authClient }) => {
      authClient.get("/auth/notification-prefs")
        .then(r => {
          prefsRef.current = r.data?.notification_prefs || {};
          localStorage.setItem("lostdc:notif-prefs", JSON.stringify(prefsRef.current));
        })
        .catch(() => {});
    });
  }, [user]);

  useEffect(() => {
    if (!user) return () => {};
    // Token is re-read on every (re)connect so token rotation is transparent.
    closedByUsRef.current = false;

    const connect = () => {
      const token = localStorage.getItem("lostdc:token");
      if (!token) return;
      const url = backendWsUrl(token);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        attemptsRef.current = 0;
      };
      ws.onclose = (e) => {
        setConnected(false);
        if (closedByUsRef.current) return;
        // If the token is invalid (4401), refresh via /auth/refresh then reconnect
        if (e && e.code === 4401) {
          import("@/lib/auth").then(({ authClient }) => {
            authClient.post("/auth/refresh")
              .then((r) => {
                const t = r.data?.token;
                if (t) localStorage.setItem("lostdc:token", t);
              })
              .catch(() => {})
              .finally(() => {
                attemptsRef.current += 1;
                const delay = Math.min(30000, 1000 * Math.pow(1.5, attemptsRef.current));
                setTimeout(() => { if (!closedByUsRef.current) connect(); }, delay);
              });
          });
          return;
        }
        attemptsRef.current += 1;
        const delay = Math.min(30000, 1000 * Math.pow(1.5, attemptsRef.current));
        setTimeout(() => { if (!closedByUsRef.current) connect(); }, delay);
      };
      ws.onerror = () => { /* onclose will handle */ };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        const { op } = msg;
        if (op === "hello") {
          const map = new Map();
          for (const uid of (msg.online_user_ids || [])) map.set(uid, { online: true });
          setPresence(map);
        } else if (op === "presence") {
          setPresence(prev => {
            const next = new Map(prev);
            next.set(msg.user_id, { online: !!msg.online, last_seen: msg.last_seen });
            return next;
          });
        } else if (op === "notification") {
          const n = msg.notification || {};
          playNotifSound(n.type, prefsRef.current);
          showBrowserNotif(n, prefsRef.current);
        }
        // Fan out to subscribers
        const subs = subsRef.current.get(op);
        if (subs) subs.forEach((cb) => { try { cb(msg); } catch { /* ignore */ } });
      };
    };

    connect();

    // Ping every 25s to keep alive
    const ping = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ op: "ping" })); } catch { /* ignore */ }
      }
    }, 25000);

    return () => {
      closedByUsRef.current = true;
      clearInterval(ping);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [user]);

  const send = (op, payload = {}) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ op, ...payload })); } catch { /* ignore */ }
    }
  };

  const subscribe = (op, cb) => {
    let set = subsRef.current.get(op);
    if (!set) { set = new Set(); subsRef.current.set(op, set); }
    set.add(cb);
    return () => set.delete(cb);
  };

  const value = useMemo(() => ({
    connected, presence, send, subscribe,
    isOnline: (uid) => !!presence.get(uid)?.online,
    lastSeen: (uid) => presence.get(uid)?.last_seen,
    probePresence: async (uid) => {
      if (!uid) return null;
      try {
        const { authClient } = await import("@/lib/auth");
        const r = await authClient.get(`/ws/presence/${uid}`);
        setPresence(prev => {
          const next = new Map(prev);
          next.set(uid, { online: !!r.data.online, last_seen: r.data.last_seen });
          return next;
        });
        return r.data;
      } catch { return null; }
    },
    setPrefs: (p) => { prefsRef.current = p; localStorage.setItem("lostdc:notif-prefs", JSON.stringify(p)); },
    getPrefs: () => prefsRef.current,
  }), [connected, presence]);

  return <RealtimeCtx.Provider value={value}>{children}</RealtimeCtx.Provider>;
}

export function useRealtime() {
  const v = useContext(RealtimeCtx);
  if (!v) return { connected: false, presence: new Map(), send: () => {}, subscribe: () => () => {}, isOnline: () => false, lastSeen: () => null, probePresence: async () => null, setPrefs: () => {}, getPrefs: () => ({}) };
  return v;
}
